/* eslint-disable import/prefer-default-export */
import * as Sentry from '@sentry/react';
import jwtDecode from 'jwt-decode';
import { throttle } from 'lodash';

import { logoutApi, refreshTokenApi } from 'api/auth';
import { getCookie, setCookie } from 'api/utils';
import { REAUTH_REQUIRED_CODE, getGraphqlErrorCode } from 'common/utils/googleSignIn';
import { authActions } from 'store/slices/authSlice';
import { ordersListActions } from 'store/slices/ordersListSlice';
import { partnerActions } from 'store/slices/partnerSlice';
import { patientActions } from 'store/slices/patientSlice';
import { patientsListActions } from 'store/slices/patientsListSlice';
import { pharmacyActions } from 'store/slices/pharmacySlice';
import { webSocketActions } from 'store/slices/webSocketSlice';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { clearAllLockedItems } from './lockedLineItemsThunks';

export const resetAppState = () => (dispatch) => {
  setCookie({ name: 'pomAccessToken', value: '', expDays: -1 });
  setCookie({ name: 'tokenSfgp', value: '', expDays: -1 });
  dispatch(authActions.resetAuthState());
  dispatch(patientActions.resetPatientState());
  dispatch(patientsListActions.resetPatientsListState());
  dispatch(partnerActions.resetPartnerState());
  dispatch(pharmacyActions.resetPharmacyState());
  dispatch(ordersListActions.resetOrdersListState());
  // POM-6099: relayQueue holds SYS_ADMIN-only rows (patient MPI, drug, NDC) and
  // compoundingQueue.mode can still say RELAY. Without this the next person to sign in sees the
  // previous sys-admin's relayed orders painted for a frame before the refetch lands.
  dispatch(workQueueActions.resetRelayQueueState());
  dispatch(clearAllLockedItems());
  dispatch(webSocketActions.clearWebSocketMessages());
};

export const signOut = () => (dispatch) => {
  logoutApi();
  sessionStorage.removeItem('socketSessionId');
  return dispatch(resetAppState());
};

export const loadUserFromJwt = () => (dispatch) => {
  const jwt = getCookie('pomAccessToken');

  const userData = jwtDecode(jwt);
  dispatch(authActions.loadUserData({ userData }));
  Sentry.setUser({ id: userData.userId, ip_address: '{{auto}}' });
};

export const refreshJwt = () => (dispatch) => {
  return refreshTokenApi()
    .then(() => dispatch(loadUserFromJwt()))
    .catch((error) => {
      dispatch(resetAppState());

      // The auth service discarded a dead Google refresh token (POM-6122). Callers send
      // the user through a consent login instead of the plain "Session Expired" path.
      if (getGraphqlErrorCode(error) === REAUTH_REQUIRED_CODE) {
        throw Object.assign(new Error('Reauth Required'), { code: REAUTH_REQUIRED_CODE });
      }

      throw new Error('Session Expired');
    });
};

const throttledRefresh = throttle(({ dispatch }) => dispatch(refreshJwt()), 5000, {
  trailing: false,
});

export const checkAndRefreshSession = () => async (dispatch) => {
  const jwt = getCookie('pomAccessToken');

  if (!jwt) {
    dispatch(resetAppState());
    throw new Error('No Existing Session');
  }

  return throttledRefresh({ dispatch });
};
